import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { API } from 'aws-amplify';
import type { AppDispatch, RootState } from '../store';
import { RequestStatus } from '../utils';

/**
 * The user's Lead-Share data-sharing relationships (PTEC-520) — their latest
 * consent decision per partner, and withdrawal of an active consent.
 *
 * Withdrawal appends an immutable `withdrawn` record server-side (the original
 * consent evidence is never altered — Consent Design §4) and stops any future
 * sharing. Deliberately NOT gated on the capability flag: withdrawal must stay
 * reachable even when the Opportunities surface is dark.
 */
export interface LeadShareConsentDecision {
	partnerOrganisationId: string;
	partnerName: string;
	decision: 'accepted' | 'declined' | 'withdrawn';
	consentCopyVersion: number;
	decidedAt: string;
	/**
	 * What was actually shared, in the wording the consent screen used, derived
	 * server-side from the field set STAMPED on this decision (PTEC-584). Empty
	 * for a decline or a withdrawal. Not field codes: the panel must describe the
	 * disclosure the same way the screen that obtained consent did.
	 */
	sharedFields: string[];
	fieldSetVersion: number;
	withdrawable: boolean;
}

interface LeadShareConsentsState {
	items: LeadShareConsentDecision[];
	status: RequestStatus;
	withdrawStatus: RequestStatus;
}

const initialState: LeadShareConsentsState = {
	items: [],
	status: RequestStatus.IDLE,
	withdrawStatus: RequestStatus.IDLE
};

export const fetchConsentDecisions = createAsyncThunk<
	LeadShareConsentDecision[],
	void,
	{ dispatch: AppDispatch; state: RootState }
>('leadShareConsents/fetch', async () => {
	const response = await API.get('inspirusapi', '/lead-share/consent', {});
	return Array.isArray(response?.items) ? response.items : [];
});

export const withdrawConsent = createAsyncThunk<
	{ partnerOrganisationId: string; capturedAt: string },
	{ partnerOrganisationId: string },
	{ dispatch: AppDispatch; state: RootState }
>('leadShareConsents/withdraw', async (payload) => {
	const response = await API.post('inspirusapi', '/lead-share/consent/withdraw', { body: payload });
	return { partnerOrganisationId: payload.partnerOrganisationId, capturedAt: response?.capturedAt ?? '' };
});

const leadShareConsentsSlice = createSlice({
	name: 'leadShareConsents',
	initialState,
	reducers: {
		resetLeadShareConsents: () => initialState
	},
	extraReducers: (builder) => {
		builder.addCase(fetchConsentDecisions.pending, (state) => {
			state.status = RequestStatus.PENDING;
		});
		builder.addCase(fetchConsentDecisions.fulfilled, (state, action) => {
			state.items = action.payload;
			state.status = RequestStatus.SUCCESS;
		});
		builder.addCase(fetchConsentDecisions.rejected, (state) => {
			state.status = RequestStatus.ERROR;
		});
		builder.addCase(withdrawConsent.pending, (state) => {
			state.withdrawStatus = RequestStatus.PENDING;
		});
		builder.addCase(withdrawConsent.fulfilled, (state, action) => {
			state.withdrawStatus = RequestStatus.SUCCESS;
			const item = state.items.find((i) => i.partnerOrganisationId === action.payload.partnerOrganisationId);
			if (item) {
				item.decision = 'withdrawn';
				item.withdrawable = false;
				if (action.payload.capturedAt) {
					item.decidedAt = action.payload.capturedAt;
				}
			}
		});
		builder.addCase(withdrawConsent.rejected, (state) => {
			state.withdrawStatus = RequestStatus.ERROR;
		});
	}
});

export const { resetLeadShareConsents } = leadShareConsentsSlice.actions;
export default leadShareConsentsSlice.reducer;
